import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SectionHeader from "@/components/site/SectionHeader";
import { Reveal } from "@/components/site/Reveal";
import Seo from "@/components/seo/Seo";
import { faqSchema, breadcrumbSchema } from "@/components/seo/schema";
import { FREE_FAQS } from "@/lib/freeProgram";

const GENERAL_FAQS = [
  { q: "How much does a website cost?", a: "Through our Free Custom Website Program the build is $0 upfront — you only pay $200/month for hosting, maintenance, and support with a 6-month minimum. Prefer to own immediately? Traditional website packages start at $499 (Starter), $999 (Business), and $1,499 (Premium)." },
  { q: "Do you offer hosting?", a: "Yes — Equinox Hosting Basic ($29/mo), Business ($59/mo), and Premium ($99/mo) with daily backups, SSL, and security monitoring. Hosting and maintenance are included in the Free Custom Website Program." },
  { q: "Can I use my own domain?", a: "Absolutely. You purchase and own your domain name, and we handle DNS configuration, deployment, and technical setup. We can also help you register a new domain." },
  { q: "Do you build e-commerce websites?", a: "Yes. E-commerce is typically scoped as part of our Premium Website or Business App packages depending on complexity." },
  { q: "Can you build a CRM or custom web app?", a: "Yes. Our CRM and App Development packages cover lead tracking, sales pipelines, dashboards, user roles, and bespoke workflows." },
  { q: "Do you offer CMS software?", a: "Yes — CMS Starter, Business, and Enterprise tiers let you update pages, blogs, and content without a developer." },
  { q: "Can you integrate APIs?", a: "We integrate Stripe, CRMs, booking systems, email platforms, databases, and business software. Single, Multi-System, and Enterprise tiers available." },
  { q: "Can you add AI features?", a: "Yes — chatbots, automation workflows, AI customer support, and lead-qualification systems. AI Starter, Business, and Enterprise tiers." },
  { q: "Who do you work with?", a: "We serve blue-collar businesses, local service companies, small businesses, startups, and corporate businesses — with nationwide remote web development across the United States." },
  { q: "Do you offer discounts?", a: "Yes — a 15% discount for military, veterans, first responders, teachers, and healthcare workers. Verification may be required." },
  { q: "How long does a project take?", a: "Most websites launch in 2–6 weeks; apps and CRMs typically 4–12 weeks depending on scope. We'll provide a clear timeline after you apply or request a quote." },
];

const FAQS = [...FREE_FAQS, ...GENERAL_FAQS];

export default function FAQ() {
  return (
    <div data-testid="page-faq">
      <Seo
        title="FAQ — Free Custom Website Program | Equinox Systems"
        description="Answers about the Free Custom Website Program: is it really free, who owns the website and domain, what happens after six months, cancellation, SEO, hosting, pricing and more."
        path="/faq"
        schemas={[faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader as="h1" eyebrow="FAQ" title="Frequently asked questions" align="center" />
          </Reveal>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.3)}>
                <AccordionItem
                  value={`item-${i}`}
                  className="border-none rounded-2xl glass overflow-hidden transition-colors hover:border-blue-500/30"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="px-6 py-5 text-left font-display text-base hover:no-underline hover:text-blue-300"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-content-${i}`}
                    className="px-6 pb-5 text-gray-400 leading-relaxed"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              </Reveal>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
